import { useMemo, useState } from 'react'
import { AlertTriangle, Eye, Film, TrendingDown, TrendingUp, Tv } from 'lucide-react'
import {
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'

import {
  useChannelCompare,
  type ChannelComparisonRow,
  type ChannelStatus,
} from '@/api/endpoints/analytics'
import { Section } from '@/components/ds/Section'
import { Segmented } from '@/components/ui/Segmented'
import { KPIGrid } from '@/features/analytics/shared/KPIGrid'
import { cn } from '@/lib/cn'

import styles from './ChannelCompareSection.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface ChannelCompareSectionProps {
  /** Platform to compare channels within (e.g. 'youtube'). Neutral so the
   * TikTok analytics page can mount the same component once its loader ships. */
  platform: string
  /** Lookback window in days until the user picks one — after that the
   * last pick (localStorage) wins. Server accepts 0 ("Tudo") or [7,365]. */
  days?: number
}

const DAYS_OPTIONS = [
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '180', label: '180d' },
  { value: '365', label: '365d' },
  { value: '0', label: 'Tudo' },
] as const

/** "na janela de 30 dias" / "em todo o histórico" — ``0`` is "Tudo". */
function windowPhrase(days: number, prefix: 'na' | 'da'): string {
  return days === 0 ? `${prefix === 'na' ? 'em' : 'de'} todo o histórico` : `${prefix} janela de ${String(days)} dias`
}

// Last window picked, so reopening the tab lands where the user left it.
// Read synchronously on mount instead of via ``useLocalStorage`` (which
// reads in an effect): the default would fire a /compare request of its
// own before the stored window replaced it.
const DAYS_STORAGE_KEY = 'prism:analytics:compare-days'

function readStoredDays(fallback: number): number {
  try {
    const raw = window.localStorage.getItem(DAYS_STORAGE_KEY)
    // Number(null) is 0, which is now "Tudo": nothing saved must stay the fallback.
    if (raw === null) return fallback
    const stored = Number(raw)
    return DAYS_OPTIONS.some((o) => Number(o.value) === stored) ? stored : fallback
  } catch {
    return fallback
  }
}

function storeDays(days: number): void {
  try {
    window.localStorage.setItem(DAYS_STORAGE_KEY, String(days))
  } catch {
    /* quota / access denied — the pick just won't survive a reload */
  }
}

const STATUS_META: Record<ChannelStatus, { label: string; color: string; bg: string }> = {
  growing: { label: 'Crescendo', color: 'var(--success)', bg: 'var(--success-bg)' },
  stable: { label: 'Estável', color: 'var(--info)', bg: 'var(--info-bg)' },
  stalling: { label: 'Estagnado', color: 'var(--warning)', bg: 'var(--warning-bg)' },
  dormant: { label: 'Dormindo', color: 'var(--fg-muted)', bg: 'var(--surface-alt)' },
}

/**
 * "Quais canais estão indo bem e quais estagnaram?" — comparativo lado a
 * lado de todos os canais da plataforma numa janela. Ranking com status
 * (Crescendo / Estável / Estagnado / Dormindo), um scatter de alcance ×
 * retenção (ou engajamento, quando não há retenção coletada), momentum por
 * coorte de publicação e eficiência (views por US$ de geração).
 *
 * Platform-neutral de propósito: o backend normaliza cada plataforma no
 * mesmo formato, então a aba do TikTok monta este mesmo componente. Painéis
 * que dependem de dados ausentes (retenção/momentum em dev sem snapshots)
 * somem via ``metric_availability``.
 */
export function ChannelCompareSection({ platform, days: initialDays = 30 }: ChannelCompareSectionProps) {
  const [days, setDays] = useState<number>(() => readStoredDays(initialDays))
  const query = useChannelCompare({ platform, days })

  const channels = useMemo(() => query.data?.channels ?? [], [query.data])
  const availability = query.data?.metric_availability ?? {}
  const showRetention = availability['retention'] ?? false
  const showMomentum = availability['momentum'] ?? false
  const showCost = availability['cost'] ?? false

  const totals = useMemo(() => {
    const views = channels.reduce((acc, c) => acc + c.views_sum, 0)
    const videos = channels.reduce((acc, c) => acc + c.video_count_window, 0)
    const atRisk = channels.filter((c) => c.status === 'stalling' || c.status === 'dormant').length
    return { views, videos, atRisk }
  }, [channels])

  const subtitle = query.data
    ? `${query.data.days === 0 ? 'Todo o histórico' : `${String(query.data.days)} dias`} · ${String(channels.length)} canal${channels.length === 1 ? '' : 'is'}`
    : `Janela: ${days === 0 ? 'todo o histórico' : `${String(days)} dias`}`

  return (
    <Section title="Comparativo entre canais" description={subtitle}>
      <div className={styles.shell}>
        <p className={styles.headerHelp}>
          Todos os canais da plataforma lado a lado na janela escolhida. O{' '}
          <strong>status</strong> combina momentum, retenção e tempo desde o último
          upload pra separar quem cresce de quem estagnou ou parou.
        </p>

        <div className={styles.controls}>
          <Segmented
            aria-label="Janela de análise"
            options={DAYS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={String(days)}
            onValueChange={(v) => {
              setDays(Number(v))
              storeDays(Number(v))
            }}
            size="sm"
          />
        </div>

        {query.isError ? (
          <QueryErrorNotice
            error={query.error}
            label="o comparativo"
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : query.isPending ? (
          <div className={styles.skeleton} aria-hidden />
        ) : channels.length === 0 ? (
          <div className={styles.empty}>
            Nenhum canal nesta plataforma ainda. Crie um canal e publique vídeos pra
            ver o comparativo.
          </div>
        ) : (
          <>
            <KPIGrid
              items={[
                { icon: <Tv />, label: 'Canais', value: channels.length, hint: 'nesta plataforma' },
                {
                  icon: <Eye />,
                  label: 'Views no portfólio',
                  value: totals.views,
                  hint: `soma ${windowPhrase(days, 'da')}`,
                },
                {
                  icon: <Film />,
                  label: 'Vídeos publicados',
                  value: totals.videos,
                  hint: windowPhrase(days, 'na'),
                },
                {
                  icon: <AlertTriangle />,
                  label: 'Canais em risco',
                  value: totals.atRisk,
                  hint: 'estagnados ou dormindo',
                },
              ]}
            />

            <RankingTable channels={channels} />

            <PositioningScatter channels={channels} showRetention={showRetention} />

            {showMomentum ? (
              <MomentumPanel channels={channels} />
            ) : (
              <p className={styles.mutedNote}>
                Momentum por coorte fica disponível quando os snapshots de performance
                (t48h/t7d) estão sendo coletados. Em dev, ative{' '}
                <code>ANALYTICS_REAL_REQUESTS=1</code>.
              </p>
            )}

            {showCost ? (
              <EfficiencyPanel channels={channels} />
            ) : (
              <p className={styles.mutedNote}>
                Eficiência (views por US$) aparece quando há custo de geração registrado
                na janela.
              </p>
            )}
          </>
        )}
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Ranking table — bordered rows, winners first (backend already sorts).
// ---------------------------------------------------------------------------

function RankingTable({ channels }: { channels: ChannelComparisonRow[] }) {
  return (
    <div className={styles.table} role="table" aria-label="Ranking de canais">
      <div className={cn(styles.row, styles.headRow)} role="row">
        <span className={styles.colRank} role="columnheader">
          #
        </span>
        <span role="columnheader">Canal</span>
        <span role="columnheader">Status</span>
        <span className={styles.colNum} role="columnheader">
          Mediana
        </span>
        <span className={styles.colNum} role="columnheader">
          Engaj.
        </span>
        <span className={styles.colNum} role="columnheader">
          Momentum
        </span>
      </div>
      {channels.map((c, idx) => (
        <div className={styles.row} role="row" key={c.channel_slug}>
          <span className={styles.colRank} role="cell">
            {idx + 1}
          </span>
          <span className={styles.chanCell} role="cell">
            <span className={styles.chanName}>{c.channel_name}</span>
            <span className={styles.chanSub}>
              {formatCount(c.views_sum)} views · {String(c.video_count_window)} vídeo
              {c.video_count_window === 1 ? '' : 's'}
            </span>
          </span>
          <span role="cell">
            <StatusPill status={c.status} reason={c.status_reason} />
          </span>
          <span className={styles.colNum} role="cell">
            {formatCount(Math.round(c.views_median))}
          </span>
          <span className={styles.colNum} role="cell" title="Like rate (likes / views) na janela">
            {formatRate(c.like_rate)}
          </span>
          <span className={styles.colNum} role="cell">
            <Momentum row={c} />
          </span>
        </div>
      ))}
    </div>
  )
}

function StatusPill({ status, reason }: { status: ChannelStatus; reason: string }) {
  const meta = STATUS_META[status]
  return (
    <span
      className={styles.pill}
      style={{ background: meta.bg, color: meta.color }}
      title={reason}
    >
      {meta.label}
    </span>
  )
}

/**
 * Momentum as the user reads it: a percentage up to +100%, a multiplier past
 * that. A channel that went from a median of 3 views to 1.000 is "×333",
 * not "+33.233%". The tooltip shows the two medians behind the number.
 */
export function formatMomentum(value: number): string {
  if (value >= 1) {
    const times = value + 1
    return `×${times.toLocaleString('pt-BR', { maximumFractionDigits: times < 10 ? 1 : 0 })}`
  }
  return `${value >= 0 ? '+' : ''}${String(Math.round(value * 100))}%`
}

function momentumTitle(row: ChannelComparisonRow): string {
  const n = row.momentum_cohort_days
  const recent = row.momentum_recent_median
  const previous = row.momentum_previous_median
  const fmt = (v: number) => Math.round(v).toLocaleString('pt-BR')
  const medians =
    recent != null && previous != null
      ? `: mediana de ${fmt(recent)} views contra ${fmt(previous)}`
      : ''
  return `Vídeos dos últimos ${String(n)} dias contra os ${String(n)} dias anteriores${medians}.`
}

function Momentum({ row }: { row: ChannelComparisonRow }) {
  const value = row.momentum_pct
  if (value === null) return <span className={styles.muted}>—</span>
  const up = value >= 0
  const color = up ? 'var(--success)' : 'var(--danger)'
  return (
    <span className={styles.momentum} style={{ color }} title={momentumTitle(row)}>
      {up ? <TrendingUp aria-hidden /> : <TrendingDown aria-hidden />}
      {formatMomentum(value)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Positioning scatter — reach (x, log) × retention or engagement (y).
// Bubble size = videos in window; color = status. Falls back to engagement
// on the y-axis when retention isn't available so it always renders.
// ---------------------------------------------------------------------------

interface ScatterPoint {
  x: number
  y: number
  z: number
  name: string
  status: ChannelStatus
  retentionMode: boolean
}

function PositioningScatter({
  channels,
  showRetention,
}: {
  channels: ChannelComparisonRow[]
  showRetention: boolean
}) {
  const points = useMemo<ScatterPoint[]>(() => {
    return channels
      .map((c) => {
        const y = showRetention ? c.retention_median : c.like_rate
        if (c.views_median <= 0 || y === null) return null
        return {
          x: c.views_median,
          y: y * 100,
          z: Math.max(1, c.video_count_window),
          name: c.channel_name,
          status: c.status,
          retentionMode: showRetention,
        }
      })
      .filter((p): p is ScatterPoint => p !== null)
  }, [channels, showRetention])

  const yLabel = showRetention ? 'Retenção' : 'Engajamento'
  const xMedian = useMemo(() => median(points.map((p) => p.x)), [points])
  const yMedian = useMemo(() => median(points.map((p) => p.y)), [points])

  const byStatus = useMemo(() => {
    const groups: Record<ChannelStatus, ScatterPoint[]> = {
      growing: [],
      stable: [],
      stalling: [],
      dormant: [],
    }
    for (const p of points) groups[p.status].push(p)
    return groups
  }, [points])

  if (points.length < 2) {
    return (
      <div className={styles.empty}>
        Poucos canais com dados suficientes pra posicionar no gráfico de alcance ×{' '}
        {yLabel.toLowerCase()}.
      </div>
    )
  }

  return (
    <article className={styles.panel}>
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Alcance × {yLabel.toLowerCase()}</h3>
        <span className={styles.panelKey}>bolha = nº de vídeos</span>
      </header>
      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 12, right: 16, bottom: 28, left: 8 }}>
            <XAxis
              type="number"
              dataKey="x"
              name="Mediana de views"
              scale="log"
              domain={['auto', 'auto']}
              tickFormatter={formatCount}
              stroke="var(--fg-subtle)"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={yLabel}
              unit="%"
              stroke="var(--fg-subtle)"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={42}
            />
            <ZAxis type="number" dataKey="z" range={[60, 520]} name="Vídeos" />
            <ReferenceLine x={xMedian} stroke="var(--border)" strokeDasharray="3 4" />
            <ReferenceLine y={yMedian} stroke="var(--border)" strokeDasharray="3 4" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ScatterTooltip yLabel={yLabel} />} />
            {(Object.keys(byStatus) as ChannelStatus[]).map((status) =>
              byStatus[status].length > 0 ? (
                <Scatter
                  key={status}
                  name={STATUS_META[status].label}
                  data={byStatus[status]}
                  fill={STATUS_META[status].color}
                  fillOpacity={0.85}
                  isAnimationActive={false}
                />
              ) : null,
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.legend}>
        {(Object.keys(STATUS_META) as ChannelStatus[]).map((s) => (
          <span key={s} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: STATUS_META[s].color }} />
            {STATUS_META[s].label}
          </span>
        ))}
      </div>
      {showRetention ? (
        <p className={styles.panelNote}>
          Retenção pode passar de 100%: Shorts entram em loop, então o tempo médio
          assistido supera a duração do vídeo — sinal de reassistência, não erro.
        </p>
      ) : null}
    </article>
  )
}

interface ScatterTooltipDatum {
  payload?: ScatterPoint
}

function ScatterTooltip({
  active,
  payload,
  yLabel,
}: {
  active?: boolean
  payload?: ScatterTooltipDatum[]
  yLabel?: string
}) {
  if (!active || !payload || payload.length === 0) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{d.name}</span>
      <span className={styles.tooltipRow}>
        Mediana: <strong>{formatCount(Math.round(d.x))}</strong> views
      </span>
      <span className={styles.tooltipRow}>
        {yLabel}: <strong>{d.y.toFixed(1)}%</strong>
      </span>
      <span className={styles.tooltipRow}>
        {String(d.z)} vídeo{d.z === 1 ? '' : 's'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Momentum panel — diverging bars (recent cohort vs previous). CSS bars so we
// don't spin up a second chart config; center line = 0.
// ---------------------------------------------------------------------------

/** Log scale for the bars: one channel at ×300 would flatten every other bar. */
function barMagnitude(v: number): number {
  return Math.log1p(Math.abs(v))
}

function MomentumPanel({ channels }: { channels: ChannelComparisonRow[] }) {
  const rows = channels.filter((c) => c.momentum_pct !== null)
  const maxAbs = useMemo(() => {
    let m = 0
    for (const c of rows) {
      const a = barMagnitude(c.momentum_pct ?? 0)
      if (a > m) m = a
    }
    return m
  }, [rows])

  if (rows.length === 0) return null

  return (
    <article className={styles.panel}>
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Momentum por coorte</h3>
        <span className={styles.panelKey}>
          mediana de views: últimos {String(rows[0]?.momentum_cohort_days ?? 30)} dias vs os anteriores
        </span>
      </header>
      <ul className={styles.divergingList}>
        {rows.map((c) => {
          const v = c.momentum_pct ?? 0
          const pct = maxAbs > 0 ? Math.min(1, barMagnitude(v) / maxAbs) : 0
          const up = v >= 0
          return (
            <li key={c.channel_slug} className={styles.divergingRow}>
              <span className={styles.divergingLabel}>{c.channel_name}</span>
              <div className={styles.divergingTrack} aria-hidden>
                <div className={styles.divergingHalfLeft}>
                  {!up ? (
                    <div
                      className={styles.divergingFillNeg}
                      style={{ width: `${String(pct * 100)}%` }}
                    />
                  ) : null}
                </div>
                <div className={styles.divergingHalfRight}>
                  {up ? (
                    <div
                      className={styles.divergingFillPos}
                      style={{ width: `${String(pct * 100)}%` }}
                    />
                  ) : null}
                </div>
              </div>
              <span
                className={styles.divergingValue}
                style={{ color: up ? 'var(--success)' : 'var(--danger)' }}
                title={momentumTitle(c)}
              >
                {formatMomentum(v)}
              </span>
            </li>
          )
        })}
      </ul>
    </article>
  )
}

// ---------------------------------------------------------------------------
// Efficiency panel — views per USD, magnitude bars relative to the max.
// ---------------------------------------------------------------------------

function EfficiencyPanel({ channels }: { channels: ChannelComparisonRow[] }) {
  const rows = useMemo(
    () => channels.filter((c) => c.views_per_usd !== null).sort((a, b) => (b.views_per_usd ?? 0) - (a.views_per_usd ?? 0)),
    [channels],
  )
  const maxVal = rows.length > 0 ? (rows[0]?.views_per_usd ?? 0) : 0

  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        Nenhum canal com custo de geração registrado na janela.
      </div>
    )
  }

  return (
    <article className={styles.panel}>
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Eficiência · views por US$</h3>
        <span className={styles.panelKey}>custo de geração (LLM) na janela</span>
      </header>
      <ul className={styles.bars}>
        {rows.map((c) => {
          const v = c.views_per_usd ?? 0
          const pct = maxVal > 0 ? Math.max(2, (v / maxVal) * 100) : 2
          return (
            <li key={c.channel_slug} className={styles.barRow}>
              <span className={styles.barLabel}>{c.channel_name}</span>
              <div className={styles.barTrack} aria-hidden>
                <div className={styles.barFill} style={{ width: `${String(pct)}%` }} />
              </div>
              <span className={styles.barValue}>
                {formatCount(Math.round(v))}
                <small> /US$</small>
              </span>
            </li>
          )
        })}
      </ul>
    </article>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

function formatRate(v: number | null): string {
  if (v === null) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function median(nums: number[]): number {
  if (nums.length === 0) return 0
  const s = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  if (s.length % 2 === 1) return s[mid] ?? 0
  return ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2
}
